import { useState } from 'react';
import { AppDialog } from '../../../components/AppDialog';
import { SpinInput } from '../../../components/SpinInput';
import { useNotify } from '../../../components/notifications';
import './preLoginSettings.css';

/** DevExpress WeekDays bit flags, Sunday-first — matches
 *  `PreLoginRecurrencePattern` / the legacy payload exactly. */
export const WEEKDAYS: { flag: number; label: string }[] = [
  { flag: 1,  label: 'Su' }, { flag: 2,  label: 'Mo' }, { flag: 4,  label: 'Tu' },
  { flag: 8,  label: 'We' }, { flag: 16, label: 'Th' }, { flag: 32, label: 'Fr' },
  { flag: 64, label: 'Sa' },
];

/** Legacy replaces DevExpress's "Last" with "Fifth" (Editor_RecurrencePopupControlCreated,
 *  TaskRecurranceSetupController :312/:330: Remove(Last), Add("Fifth", 5)) — the
 *  SAME numeric value 5, so the stored payload is identical. */
export const WEEK_OF_MONTH = ['First', 'Second', 'Third', 'Fourth', 'Fifth'];

export const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/** The recurrence as the "…" popup edits it — 1:1 with `PreLoginRecurrencePattern`. */
export interface Rec {
  type: number;             // 0 Daily · 1 Weekly · 2 Monthly · 3 Yearly
  periodicity: number;
  weekDays: number;         // Weekly: bit flags
  monthlyMode: 'day' | 'weekday';
  dayNumber: number;
  weekOfMonth: number;      // 1..5
  onWeekday: number;        // a single WEEKDAYS flag
  month: number;            // 1..12 (Yearly)
  range: 0 | 1 | 2;         // 0 not chosen yet · 1 end after N occurrences · 2 end by date
  occurrenceCount: number;
  recurrenceEnd: string;    // yyyy-MM-dd
}

/** Legacy hides the "No end date" radio (RangeCtl.DeNoEnd.ClientVisible = false,
 *  :290), so a schedule always ends: after N occurrences or by a date. Neither
 *  radio starts selected in the XAF popup, and OK asks for one. */
export const FRESH: Rec = {
  type: 0, periodicity: 1, weekDays: 2, monthlyMode: 'day', dayNumber: 1,
  weekOfMonth: 1, onWeekday: 2, month: 1, range: 0, occurrenceCount: 1, recurrenceEnd: '',
};

/** One sentence for the read-only Recurrence box (the server words saved rows;
 *  this words an edit not yet saved). */
export const describeRec = (r: Rec): string => {
  const unit = ['day', 'week', 'month', 'year'][r.type];
  const every = r.periodicity > 1 ? `Every ${r.periodicity} ${unit}s` : `Every ${unit}`;
  let on = '';
  if (r.type === 1) {
    const names = WEEKDAYS.filter(d => (r.weekDays & d.flag) !== 0).map(d => d.label).join(', ');
    on = names ? ` on ${names}` : '';
  } else if (r.type >= 2) {
    const where = r.monthlyMode === 'day'
      ? `day ${r.dayNumber}`
      : `the ${(WEEK_OF_MONTH[r.weekOfMonth - 1] ?? "").toLowerCase()} ${WEEKDAYS.find(d => d.flag === r.onWeekday)?.label ?? ''}`;
    on = ` on ${where}${r.type === 3 ? ` of ${MONTHS[r.month - 1]}` : ''}`;
  }
  const end = r.range === 1 ? `, ${r.occurrenceCount} occurrence${r.occurrenceCount === 1 ? '' : 's'}`
            : r.range === 2 && r.recurrenceEnd ? `, until ${r.recurrenceEnd}` : '';
  return `${every}${on}${end}`;
};

/** Opt-in differences for a screen whose legacy popup differs in small ways. Unset = the Pre-Login popup, unchanged. */
export interface RecurrenceOptions {
  /** 'disabled' shows the "No end date" radio greyed out (Maintenance: G5 keeps it visible but
   *  `DeNoEnd.ClientEnabled = false`); default hides it, as Pre-Login does. */
  noEnd?: 'hidden' | 'disabled';
  /** Three-letter weekday names (Sun … Sat) and "Recur every N week(s) on:" — the Maintenance Schedular popup's wording. */
  longDayLabels?: boolean;
}

const DAY_LABELS_LONG = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** The recurrence number boxes use the site's shared accent-colour stepper (components/SpinInput) - the same one as the
 *  Clients prospect popup's Probability - sized to the dialog's 64 x 26 boxes. */
const Spin = ({ value, onChange, min = 1, max = 999, disabled }: {
  value: number; onChange: (n: number) => void; min?: number; max?: number; disabled?: boolean;
}) => (
  <SpinInput width={64} height={26} min={min} max={max} disabled={disabled}
             value={Number.isFinite(value) ? String(value) : ''} onChange={v => onChange(v === '' ? 0 : Number(v))} />
);

/** The recurrence editor behind the "…" button — laid out like the XAF popup
 *  (owner screenshot 2026-09-29): a small "Recurrence" window, frequency radios
 *  down the left with their rule beside them, the End radios underneath, and
 *  OK · Cancel · Remove recurrence. Legacy customisations kept
 *  (TaskRecurranceSetupController.RecurrenceControl_Load): "Last" shown as
 *  "Fifth", "Every weekday" and the grouped weekday items removed. Shared by the
 *  Pre-Login Task Scheduler and the Maintenance Setup Link (G5's Schedular popup). */
export function RecurrenceDialog({ value, onOk, onCancel, onRemove, options = {} }: {
  value: Rec;
  onOk: (r: Rec) => void;
  onCancel: () => void;
  onRemove: () => void;
  options?: RecurrenceOptions;
}) {
  const notify = useNotify();
  const [r, setR] = useState<Rec>(value);
  const set = (patch: Partial<Rec>) => setR(prev => ({ ...prev, ...patch }));
  const dayLabel = (i: number) => (options.longDayLabels ? DAY_LABELS_LONG[i] : WEEKDAYS[i]?.label) ?? '';
  const everyWord = options.longDayLabels ? 'Recur every' : 'Every';

  const ok = () => {
    if (r.type === 1 && r.weekDays === 0) { notify.error('Select at least one day of the week.'); return; }
    if (r.range === 0) { notify.error('Choose how the recurrence ends: End after or End by.'); return; }
    if (r.range === 2 && !r.recurrenceEnd) { notify.error("'End by' date must not be empty."); return; }
    if (r.range === 1 && r.occurrenceCount < 1) { notify.error('Occurrences must be at least 1.'); return; }
    onOk(r);
  };

  return (
    <AppDialog
      open
      onClose={onCancel}
      title="Recurrence"
      icon="ti-repeat"
      width={470}
      footer={<>
        <button className="ca-btn ca-btn-solid" onClick={ok}>OK</button>
        <button className="ca-btn" onClick={onCancel}>Cancel</button>
        <button className="ca-btn" onClick={onRemove}>Remove recurrence</button>
      </>}
    >
      <div className="pl-rec" style={{ gridTemplateColumns: '100px minmax(0, 1fr)' }}>
        <div className="pl-rec-freq" role="radiogroup" aria-label="Repeat">
          {['Daily', 'Weekly', 'Monthly', 'Yearly'].map((label, i) => (
            <label key={label}>
              <input type="radio" name="rtype" checked={r.type === i} onChange={() => set({ type: i })} /> {label}
            </label>
          ))}
        </div>

        <div className="pl-rec-rule">
          <div className="pl-line">
            {r.type === 0 && <input type="radio" checked readOnly aria-label="Every" />}
            {everyWord}
            <Spin value={r.periodicity} onChange={n => set({ periodicity: n })} />
            {['day(s)', 'week(s)', 'month(s)', 'year(s)'][r.type]}{options.longDayLabels && r.type === 1 ? ' on:' : ''}
          </div>

          {r.type === 1 && (
            <div className="pl-days">
              {WEEKDAYS.map((d, i) => (
                <label key={d.flag}>
                  <input type="checkbox" checked={(r.weekDays & d.flag) !== 0}
                         onChange={e => set({ weekDays: e.target.checked ? r.weekDays | d.flag : r.weekDays & ~d.flag })} />
                  {dayLabel(i)}
                </label>
              ))}
            </div>
          )}

          {r.type >= 2 && (
            <>
              {r.type === 3 && (
                <div className="pl-line">
                  Of
                  <select className="pl-in pl-medium" value={r.month} onChange={e => set({ month: Number(e.target.value) })}>
                    {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                  </select>
                </div>
              )}
              <div className="pl-line">
                <label><input type="radio" name="mmode" checked={r.monthlyMode === 'day'}
                               onChange={() => set({ monthlyMode: 'day' })} /> Day</label>
                <Spin max={31} disabled={r.monthlyMode !== 'day'}
                      value={r.dayNumber} onChange={n => set({ dayNumber: n })} />
              </div>
              <div className="pl-line">
                <label><input type="radio" name="mmode" checked={r.monthlyMode === 'weekday'}
                               onChange={() => set({ monthlyMode: 'weekday' })} /> The</label>
                <select className="pl-in pl-medium" disabled={r.monthlyMode !== 'weekday'}
                        value={r.weekOfMonth} onChange={e => set({ weekOfMonth: Number(e.target.value) })}>
                  {WEEK_OF_MONTH.map((w, i) => <option key={w} value={i + 1}>{w}</option>)}
                </select>
                <select className="pl-in pl-medium" disabled={r.monthlyMode !== 'weekday'}
                        value={r.onWeekday} onChange={e => set({ onWeekday: Number(e.target.value) })}>
                  {WEEKDAYS.map((d, i) => <option key={d.flag} value={d.flag}>{dayLabel(i)}</option>)}
                </select>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="pl-rec-ends">
        {options.noEnd === 'disabled' && (
          <div className="pl-line">
            <label><input type="radio" name="range" checked={false} disabled readOnly /> No end date</label>
          </div>
        )}
        <div className="pl-line">
          <label><input type="radio" name="range" checked={r.range === 1} onChange={() => set({ range: 1 })} /> End after:</label>
          <Spin disabled={r.range !== 1}
                value={r.occurrenceCount} onChange={n => set({ occurrenceCount: n })} />
          occurrences
        </div>
        <div className="pl-line">
          <label><input type="radio" name="range" checked={r.range === 2} onChange={() => set({ range: 2 })} /> End by:</label>
          <input type="date" className="pl-in pl-medium" disabled={r.range !== 2}
                 value={r.recurrenceEnd} onChange={e => set({ recurrenceEnd: e.target.value })} />
        </div>
      </div>
    </AppDialog>
  );
}
